import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './TunnelMonitoring.module.css';
import { Badge, Button, Drawer, Empty, message, Modal, Radio, Select, Space, Spin, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { PlusOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import OrgChart, { getNode, OrgChartConfig } from 'react-balkangraph-orgchart';
import 'react-balkangraph-orgchart/dist/index.css';

import { tunnelMonitor } from '../../api';
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const HealthColorMapping = {
    0: 'green',
    1: 'orange',
    2: 'red',
};

const HealthType = {
    best: 'best',
    worst: 'worst',
};

const NodeHealthColumns = {
    best: 'node_health',
    worst: 'worst_case_node_health',
};

const NodeHealthLogicColumns = {
    best: 'node_health_logic',
    worst: 'worst_case_node_health_logic',
};

const PopHealthColumns = {
    best: 'pop_health',
    worst: 'worst_case_pop_health',
};

const RangeColorMapping = {
    min: 'green',
    avg: 'orange',
    max: 'red',
};

const UsageList = {
    cpu: { name: 'CPU %', tabName: 'CPU' },
    memory: { name: 'Memory %', tabName: 'Memory' },
    pps: { name: 'PPS (Packets/Second)', tabName: 'PPS' },
    bandwidth: { name: 'Bandwidth', tabName: 'Bandwidth' },
};

const RangeTerms = ['min', 'avg', 'max'];
const RulesForFormat = {
    pps: [0, false, 0], // formateNumber DM, formateBits;
    bandwidth: [0, true, 1],
};

function formateNumber(value, dm = 1) {
    value = parseFloat(value);
    return value ? value.toFixed(dm) : value;
}

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function formatValue(key, value, isForGraph) {
    if (RulesForFormat.hasOwnProperty(key)) {
        value = formateNumber(value, RulesForFormat[key][0]);
        if (RulesForFormat[key][1]) {
            value = formatKBits(value, isForGraph, RulesForFormat[key][2]);
        }
        return value;
    }
    return formateNumber(value, 1);
}

function formatKBits(kbit, isForGraph, limit = 0) {
    kbit = parseFloat(kbit);
    if (isForGraph) return formatKBitsForGraph(kbit);
    if (kbit === 0 || kbit < 1) {
        return kbit + ' Kb/s';
    }
    const k = 1000;
    const sizes = ['Kb/s', 'Mb/s', 'Gb/s', 'Tb/s', 'Pb/s', 'Eb/s', 'Zb/s', 'Yb/s'];
    const i = Math.floor(Math.log(kbit) / Math.log(k));
    return parseFloat((kbit / Math.pow(k, i)).toFixed(limit)) + ' ' + sizes[i];
}

function formatKBitsForGraph(kbit) {
    if (kbit === 0) return 0;
    return parseFloat((kbit / Math.pow(1000, 0)).toFixed(0));
}

const DEFAULT_HEALTH_VIEW = HealthType.worst;
const PopListMapping = {};
const PopListMappingName = {};
let FirstLoad = true;
let PopOptions = [];
let NodeMapping = {};
let cancelToken = null;
const TunnelMonitoring = (props) => {
    const functionLists = {
        getPops: 'getPops',
        getNodes: 'getNodes',
    };

    const infoTitleSize = 5;

    const [loading, setLoading] = useState(true);
    const [selectedPop, setSelectedPop] = useState([]);
    const [nodeList, setNodeList] = useState([]);
    const [popLoading, setPopLoading] = useState(false);
    const [healthType, setHealthType] = useState(DEFAULT_HEALTH_VIEW);
    const [popUsage, setPopUsage] = useState([]);
    const [healthRadioGroup, setHealthRadioGroup] = useState(2);
    const [showDrawer, setShowDrawer] = useState(false);
    const [firstLoad, setFirstLoad] = useState(FirstLoad);

    const fetchListCall = async (functionName, properties, config) => {
        try {
            const result = await tunnelMonitor(
                {
                    function: functionName,
                    ...properties,
                },
                config
            );
            if (result.success === 0) message.error(result.data.msg, 4);
            if (result.success === 1) {
                if (functionLists.getPops === functionName) {
                    PopOptions = result.data.pop.map((row) => {
                        PopListMapping[row.id] = row.name;
                        PopListMappingName[row.name] = row.id;
                        return { label: row.name, value: row.id };
                    });
                    result.data.node.map((row) => {
                        NodeMapping[row.id] = row;
                    });
                }
                if (functionLists.getNodes === functionName) {
                    setPopUsage(result.data.pop_usage);
                    setNodeList(result.data.nodes);
                    setFirstLoad(false);
                    setPopLoading(false);
                }
            }
            setLoading(false);
        } catch (error) {
            if (!axios.isCancel(error)) {
                setPopLoading(false);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchListCall(functionLists.getPops, {});
    }, []);

    useEffect(() => {
        if (!selectedPop || selectedPop.length === 0) {
            setPopLoading(true);
            if (cancelToken) {
                cancelToken.cancel('Operations cancelled due to new request');
            }
            cancelToken = axios.CancelToken.source();
            fetchListCall(functionLists.getNodes, { health: healthRadioGroup, healthType: healthType }, { cancelToken: cancelToken.token });
        }
    }, [healthType]);

    const onChangePop = (value, option) => {
        setSelectedPop(value);
        setHealthRadioGroup(null);
        if (value.length === 0) {
            onClearPop();
            return;
        }
        if (cancelToken) {
            cancelToken.cancel('Operations cancelled due to new request');
        }
        cancelToken = axios.CancelToken.source();
        setPopLoading(true);
        fetchListCall(functionLists.getNodes, { pop_id: value }, { cancelToken: cancelToken.token });
    };

    const onClearPop = () => {
        setFirstLoad(true);
        setNodeList([]);
        setSelectedPop([]);
    };

    const switchHealthView = (checked) => {
        if (checked) {
            setHealthType(HealthType.best);
        } else {
            setHealthType(HealthType.worst);
        }
    };

    const onChangeGroup = (event) => {
        setSelectedPop([]);
        setHealthRadioGroup(event.target.value);
        if (cancelToken) {
            cancelToken.cancel('Operations cancelled due to new request For health group');
        }
        cancelToken = axios.CancelToken.source();
        setPopLoading(true);
        fetchListCall(functionLists.getNodes, { health: event.target.value, healthType: healthType }, { cancelToken: cancelToken.token });
    };

    const onShowDrawer = () => {
        setShowDrawer(true);
    };

    const onDrawerClose = () => {
        setShowDrawer(false);
    };

    return (
        <>
            {loading ? (
                <div className={styles.loading}>
                    <Spin size={'large'} />
                </div>
            ) : (
                <div>
                    <div className={styles.div_bubble + ' ' + styles.ns_flex_row}>
                        <Select
                            showSearch
                            allowClear
                            autoClearSearchValue={false}
                            mode={'multiple'}
                            placeholder={'Select Pop'}
                            style={{ width: '20%' }}
                            options={PopOptions}
                            value={selectedPop}
                            optionFilterProp={'children'}
                            onChange={onChangePop}
                            onClear={onClearPop}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                        <Radio.Group buttonStyle={'solid'} onChange={onChangeGroup} value={healthRadioGroup}>
                            <Radio.Button className={healthRadioGroup === 0 ? styles.health_green : styles.health_green_text} value={0}>
                                Green
                            </Radio.Button>
                            <Radio.Button className={healthRadioGroup === 1 ? styles.health_orange : styles.health_orange_text} value={1}>
                                Orange
                            </Radio.Button>
                            <Radio.Button className={healthRadioGroup === 2 ? styles.health_red : styles.health_red_text} value={2}>
                                Red
                            </Radio.Button>
                        </Radio.Group>
                        <Space size={8} direction={'horizontal'}>
                            <Text>Health : </Text>
                            <Text type={'secondary'}>Failover Mode</Text>
                            <Switch onChange={switchHealthView} />
                            <Text type={'secondary'}>Normal Mode</Text>
                        </Space>
                        {popLoading ? <Spin /> : null}
                        <InfoCircleFilled className={styles.info_icon_position} onClick={onShowDrawer} />
                    </div>
                    <div>
                        {nodeList.length > 1 && !popLoading ? (
                            <PopTree popUsage={popUsage} nodeList={nodeList} healthColumn={healthType} />
                        ) : (
                            <>
                                {!popLoading && (selectedPop.length != 0 || healthRadioGroup != null) ? (
                                    <div className={styles.div_bubble}>
                                        <Empty />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            )}
            <Drawer title="Information" placement="right" onClose={onDrawerClose} open={showDrawer} size={'large'}>
                <Space size={16} direction={'vertical'}>
                    <div>
                        <Title level={infoTitleSize} className={styles.margin_bottom_h}>
                            <Text>Terminology :</Text>
                        </Title>
                        <ul style={{ marginBottom: '0px' }}>
                            <li>
                                <label className={styles.font_bold}>CPU: </label>Percentage of CPU Usage
                            </li>
                            <li>
                                <label className={styles.font_bold}>Memory: </label>Percentage of Memory Usage
                            </li>
                            <li>
                                <label className={styles.font_bold}>PPS: </label>Packets per Second
                            </li>
                            <li>
                                <label className={styles.font_bold}>Bandwidth: </label>If not specified, it is measured in kbps (kilobits
                                per second)
                            </li>
                            <li>
                                <label className={styles.font_bold}>Primary Tunnels: </label>Tunnels with higher traffic compared to their
                                counterparts in other nodes.
                            </li>
                            <li>
                                <label className={styles.font_bold}>Backup Tunnels: </label>Tunnels with lower traffic compared to their
                                counterparts in other nodes.
                            </li>
                            <li>
                                <label className={styles.font_bold}>Normal Case: </label>Only primary tunnels carry traffic, while backup
                                tunnels have minimal to no traffic.
                            </li>
                            <li>
                                <label className={styles.font_bold}>Failover Case: </label>Assuming all backup tunnels become primary and
                                handle the same traffic as their respective primary tunnels.
                            </li>
                        </ul>
                    </div>
                    <div>
                        <Title level={infoTitleSize} className={styles.margin_bottom_h}>
                            <Text>Node Health Calculation:</Text>
                        </Title>
                        <p>
                            Node health is determined using 24-hour data with hourly granularity for CPU and memory usage percentages, as
                            well as tunnel count. Nodes are categorized as follows:
                        </p>
                        <Space size={0} direction={'vertical'} className={styles.ns_margin_left_large}>
                            <Badge
                                color="red"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_red}>Red: </label>If CPU or memory usage
                                        exceeds the 50% threshold six times or more in 24 hours or if the tunnel count is greater than or
                                        equal to 500.
                                    </>
                                }
                            />
                            <Badge
                                color="orange"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_orange}>Orange: </label>If CPU or memory usage
                                        exceeds the 50% threshold three to five times in 24 hours or if the tunnel count falls within the
                                        range of 350 to 500.
                                    </>
                                }
                            />
                            <Badge
                                color="green"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_green}>Green: </label>If a node does not meet
                                        the criteria for being categorized as Red or Orange.
                                    </>
                                }
                            />
                        </Space>
                    </div>
                    <div>
                        <Title level={infoTitleSize} className={styles.margin_bottom_h}>
                            <Text>Failover Case/Node</Text>
                        </Title>
                        <Space size={0} direction={'vertical'} className={styles.ns_margin_left_large}>
                            <Badge
                                color="red"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_red}>Red: </label>If CPU or memory usage
                                        exceeds the 75% threshold six times or more in 24 hours or if the tunnel count is greater than or
                                        equal to 500.
                                    </>
                                }
                            />
                            <Badge
                                color="orange"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_orange}>Orange: </label>If CPU or memory usage
                                        exceeds the 75% threshold three to five times in 24 hours or if the tunnel count falls within the
                                        range of 350 to 500.
                                    </>
                                }
                            />
                            <Badge
                                color="green"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_green}>Green: </label>If a node does not meet
                                        the criteria for being categorized as Red or Orange.
                                    </>
                                }
                            />
                        </Space>
                    </div>
                    <div>
                        <Title level={infoTitleSize} className={styles.margin_bottom_h}>
                            <Text>POP Health Calculation:</Text>
                        </Title>
                        <p>
                            The health status of a pop is determined by the health of the underlying nodes present in the pop. The pops are
                            categorized as follows:
                        </p>
                        <Space size={0} direction={'vertical'} className={styles.ns_margin_left_large}>
                            <Badge
                                color="red"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_red}>Red: </label>If it contains one or more
                                        red nodes or if more than 50% of its nodes are orange.
                                    </>
                                }
                            />
                            <Badge
                                color="orange"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_orange}>Orange: </label>If less than 50% of
                                        its nodes are orange.
                                    </>
                                }
                            />
                            <Badge
                                color="green"
                                text={
                                    <>
                                        <label className={styles.font_bold + ' ' + styles.dot_green}>Green: </label>If it does not meet the
                                        criteria for being categorized as Red or Orange.
                                    </>
                                }
                            />
                        </Space>
                    </div>
                </Space>
            </Drawer>
        </>
    );
};

const PopTree = (popTreeProps) => {
    const [treeData, setTreeData] = useState([]);
    const [nodeList, setNodeList] = useState([]);
    const nodeSize = { height: 65, width: 200 };
    const getNodeHealth = (nodes) => {
        if (nodes.hasOwnProperty('IPSecNodeUsages')) {
            if (nodes.IPSecNodeUsages.length > 0) {
                const latestNode = nodes.IPSecNodeUsages[0];
                if (HealthColorMapping.hasOwnProperty(latestNode[NodeHealthColumns[popTreeProps.healthColumn]])) {
                    return HealthColorMapping[latestNode[NodeHealthColumns[popTreeProps.healthColumn]]];
                }
            }
        }
        return '';
    };

    const getPopHealth = (popName) => {
        if (PopListMappingName.hasOwnProperty(popName)) {
            const health = popTreeProps.popUsage?.find((usage) => usage.pop_id === PopListMappingName[popName]);
            if (health && HealthColorMapping.hasOwnProperty(health[PopHealthColumns[popTreeProps.healthColumn]])) {
                return HealthColorMapping[health[PopHealthColumns[popTreeProps.healthColumn]]];
            }
        }
        return '';
    };

    useEffect(() => {
        setTreeData([]);
        setTimeout(() => {
            const popTree = [];
            const popList = [];
            popTreeProps.nodeList.map((nodes) => {
                if (!popList.includes(PopListMapping[nodes.pop_id])) {
                    popList.push(PopListMapping[nodes.pop_id]);
                }
            });
            popList.map((pop, index) => {
                popTree.push({
                    id: pop,
                    name: pop,
                    pid: index,
                    isPop: true,
                    node: getNode({
                        ...nodeSize,
                        component: CustomNodes({ name: pop, isPop: true, health: getPopHealth(pop) }),
                    }),
                });
            });
            popTreeProps.nodeList.map((nodes) => {
                nodes.health = getNodeHealth(nodes);
                popTree.push({
                    id: nodes.id,
                    name: nodes.name,
                    pid: PopListMapping[nodes.pop_id],
                    isPop: false,
                    node: getNode({ ...nodeSize, component: CustomNodes(nodes) }),
                });
            });
            setTreeData(popTree);
        }, 50);
    }, [popTreeProps]);

    const onClickGraphNode = (value) => {
        const selectedEdge = treeData.find((node) => node.id === value.node.id);
        if (selectedEdge) {
            if (selectedEdge.isPop) {
                setNodeList(popTreeProps.nodeList.filter((node) => node.pop_id === PopListMappingName[selectedEdge.id]));
            } else {
                setNodeList(popTreeProps.nodeList.filter((node) => node.id === selectedEdge.id));
            }
        }
    };

    return (
        <>
            {treeData.length > 2 ? (
                <div className={styles.div_bubble + ' ' + styles.box_min_height}>
                    <OrgChart
                        customTemplate
                        enabledCustomTemplate={true}
                        onCardClick={onClickGraphNode}
                        nodes={treeData}
                        className={styles.pop_tree}
                        id={'pop_tree'}
                        nodeHeight={nodeSize.height}
                        nodeWidth={nodeSize.width}
                        config={{
                            nodeBinding: {
                                node: 'node',
                            },
                            collapse: {
                                level: popTreeProps.popUsage.length > 1 ? 1 : 2,
                            },
                            toolbar: {
                                layout: false,
                                zoom: false,
                                fit: true,
                                expandAll: true,
                                fullScreen: true,
                            },
                            mouseScrool: OrgChartConfig.action.ctrlZoom,
                            scaleInitial: OrgChartConfig.match.width,
                            scaleMin: 0.4,
                            scaleMax: 0.75,
                            // scaleInitial: 0.75,
                            // orientation: OrgChartConfig.orientation.left,
                            siblingSeparation: 10,
                        }}
                    />
                </div>
            ) : null}
            {nodeList.map(function (node) {
                return <Nodes data={node} healthColumn={popTreeProps.healthColumn}></Nodes>;
            })}
        </>
    );
};
const CustomNodes = (nodeValue) => {
    let healthColor = styles.custom_tree;
    healthColor = healthColor + ' ' + styles['health_node_' + nodeValue?.health];
    return (
        <>
            <div className={healthColor}>
                <div className={styles.custom_tree_name}>{nodeValue.name}</div>
            </div>
        </>
    );
};
const Nodes = (nodesProps) => {
    const [display, setDisplay] = useState(false);
    const [latestNodeData, setLatestNodeData] = useState(
        nodesProps?.data?.IPSecNodeUsages.length > 0 ? nodesProps?.data?.IPSecNodeUsages[0] : null
    );
    const [trendView, setTrendView] = useState(false);

    useEffect(() => {
        const latestNode = nodesProps?.data?.IPSecNodeUsages.length > 0 ? nodesProps?.data?.IPSecNodeUsages[0] : null;
        if (latestNode) {
            setLatestNodeData(latestNode);
        }
    }, [nodesProps]);

    const collapse = (event) => {
        setDisplay(!display);
    };

    const onTrendView = () => {
        setTrendView(!trendView);
    };

    return (
        <>
            <div className={styles.div_bubble_node}>
                <div className={styles.ns_flex_justify}>
                    <div
                        className={
                            styles.collapse_title +
                            ' ' +
                            styles['health_node_block_' + HealthColorMapping[latestNodeData[NodeHealthColumns[nodesProps.healthColumn]]]]
                        }
                        onClick={collapse}
                    >
                        {nodesProps.data.name}
                    </div>
                    <Space size={16}>
                        <Text type={'secondary'}>
                            <span className={styles.font_bold}>CPU: </span> {nodesProps.data.cpu}{' '}
                            <span className={styles.unit_label}>(cores)</span>
                        </Text>
                        <Text type={'secondary'}>
                            <span className={styles.font_bold}>Memory: </span> {nodesProps.data.memory}{' '}
                            <span className={styles.unit_label}>(GB)</span>
                        </Text>
                    </Space>
                </div>
                <div className={display ? styles.expand : styles.collapse}>
                    <Space size={8} direction={'vertical'} style={{ width: '100%' }}>
                        <LatestNodeDataView data={latestNodeData} healthType={nodesProps.healthColumn} />
                        <Button type={'primary'} shape={'round'} icon={<PlusOutlined />} size={'middle'} onClick={onTrendView}>
                            Usage Trend
                        </Button>
                        <div className={trendView ? styles.expand : styles.collapse} style={{ textAlign: 'center' }}>
                            <UsageDataView nodeId={nodesProps.data.id} healthType={nodesProps.healthColumn} isShown={trendView} />
                        </div>
                    </Space>
                </div>
            </div>
        </>
    );
};

const LatestNodeDataView = (node) => {
    const names = new Set();
    const latestNodeUsageColumn = [
        {
            title: 'Date : ' + node.data.date,
            dataIndex: 'usage',
            key: 'usage',
            width: '10%',
        },
        {
            title: 'Min',
            dataIndex: 'min',
            key: 'min',
            width: '10%',
        },
        {
            title: 'Avg',
            dataIndex: 'avg',
            key: 'avg',
            width: '10%',
        },
        {
            title: 'Max',
            dataIndex: 'max',
            key: 'max',
            width: '10%',
        },
        {
            title: 'Avg(Failover)',
            dataIndex: 'worst_avg',
            key: 'worst_avg',
            width: '10%',
            render: (text, record) => <>{text === undefined ? '-' : text}</>,
        },
        {
            title: 'Max(Failover)',
            dataIndex: 'worst_max',
            key: 'worst_max',
            width: '10%',
            render: (text, record) => <>{text === undefined ? '-' : text}</>,
        },
    ];

    const ActiveColumn = [
        {
            title: 'Tunnel Name',
            dataIndex: 'tunnel_name',
            key: 'tunnel_name',
            width: '10%',
        },
        {
            title: 'Pop',
            dataIndex: 'ipsec_node_id',
            key: 'ipsec_node_id',
            width: '10%',
            render: (text, record) => <>{PopListMapping[NodeMapping[text].pop_id]}</>,
        },
        {
            title: 'Node',
            dataIndex: 'ipsec_node_id',
            key: 'ipsec_node_id',
            width: '20%',
            render: (text, record) => <>{NodeMapping[text].name}</>,
        },
        {
            title: 'Tenant Id',
            dataIndex: 'tenant_id',
            key: 'tenant_id',
            width: '10%',
        },
        {
            title: 'Min',
            dataIndex: 'daily_min_bandwidth',
            key: 'daily_min_bandwidth',
            width: '10%',
            render: (text, record) => <>{formatKBits(text, false, 2)}</>,
        },
        {
            title: 'Avg',
            dataIndex: 'daily_avg_bandwidth',
            key: 'daily_avg_bandwidth',
            width: '10%',
            render: (text, record) => <>{formatKBits(text, false, 2)}</>,
        },
        {
            title: 'Max',
            dataIndex: 'daily_max_bandwidth',
            key: 'daily_max_bandwidth',
            width: '10%',
            render: (text, record) => <>{formatKBits(text, false, 2)}</>,
        },
    ];

    const modalTypeList = {
        active: 'Primary',
        backup: 'Backup',
    };

    const defaultModalData = {
        active: [],
        backup: [],
    };

    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(modalTypeList.active);
    const [modalData, setModalData] = useState(defaultModalData);

    const fetchActiveBackupData = async () => {
        setLoading(true);
        const result = await tunnelMonitor({
            function: 'getActiveBackupTunnel',
            type: modalType,
            ipsec_node_id: node.data.ipsec_node_id,
            date: node.data.date,
        });
        if (result.success === 0) message.error(result.data.msg, 4);
        if (result.success === 1) {
            setModalData(result.data.activeBackupData);
        }
        setLoading(false);
    };

    useEffect(() => {
        names.clear();
        if (!node.data) return;
        const nodeData = [];
        Object.entries(UsageList).map(([key, value]) => {
            const latestNode = node.data;
            const data = {
                date: latestNode.date,
                usage: value.name,
            };
            RangeTerms.map((range) => {
                let format = latestNode['daily_' + range + '_' + key];
                data[range] = formatValue(key, format, false);
            });
            data['worst_avg'] = formatValue(key, latestNode['worst_case_avg_' + key], false);
            data['worst_max'] = formatValue(key, latestNode['worst_case_max_' + key], false);
            data['rowType'] = key;
            nodeData.push(data);
        });
        setTableData(nodeData);
    }, [node]);

    const showModal = (value) => {
        setModalType(modalTypeList[value]);
        setModalVisible(true);
        if (modalData.active.length === 0 && modalData.backup.length === 0) {
            fetchActiveBackupData();
        } else if (modalData.active[0].ipsec_node_id !== node.data.ipsec_node_id) {
            fetchActiveBackupData();
        }
    };

    const onModalCancel = () => {
        setModalVisible(false);
    };

    const rowColor = (record, index) => {
        const indexMapping = ['cpu', 'memory'];
        let healthLogic = node.data[NodeHealthLogicColumns[node.healthType]];
        if (healthLogic) {
            healthLogic = healthLogic.split(',');
            if (record.rowType === indexMapping[0]) {
                return styles['row_color_' + HealthColorMapping[healthLogic[0]]];
            }
            if (record.rowType === indexMapping[1]) {
                return styles['row_color_' + HealthColorMapping[healthLogic[1]]];
            }
        }

        return '';
    };

    return (
        <>
            <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                <Tag color="#2db7f5" className={styles.tag_cursor} onClick={() => showModal('active')}>
                    Primary Tunnel Count : {node.data.active_tunnel_count}
                </Tag>
                <Tag color="#2db7f5" className={styles.tag_cursor} onClick={() => showModal('backup')}>
                    Backup Tunnel Count : {node.data.backup_tunnel_count}
                </Tag>
            </div>

            <Table
                rowKey={'usage'}
                size={'small'}
                bordered
                style={{ margin: '2px', fontSize: '12px' }}
                columns={latestNodeUsageColumn}
                dataSource={tableData}
                pagination={false}
                rowClassName={rowColor}
            />

            <Modal
                title={modalType + ' Tunnel'}
                open={modalVisible}
                onCancel={onModalCancel}
                okButtonProps={{ hidden: true }}
                width={'60%'}
            >
                <>
                    {loading ? (
                        <Spin />
                    ) : (
                        <>
                            {modalType === modalTypeList.active ? (
                                <Table
                                    columns={ActiveColumn}
                                    dataSource={modalData.active}
                                    size={'small'}
                                    pagination={false}
                                    scroll={{ y: 450 }}
                                ></Table>
                            ) : (
                                <Table
                                    columns={ActiveColumn}
                                    dataSource={modalData.backup}
                                    size={'small'}
                                    pagination={false}
                                    scroll={{ y: 450 }}
                                ></Table>
                            )}
                        </>
                    )}
                </>
            </Modal>
        </>
    );
};

const UsageDataView = (nodeUsage) => {
    const tabKeys = {
        node: 'Node',
        tunnel: 'Tunnel',
        health: 'Health',
    };
    const [isHourlyData, setIsHourlyData] = useState(false);
    const [showSwitch, setShowSwitch] = useState(true);
    const [usageLoading, setUsageLoading] = useState(false);
    const [nodeUsages, setNodeUsages] = useState({ nodeUsage: [], nodeMetrics: [] });
    const onChangeSwitch = (checked, event) => {
        setIsHourlyData(checked);
    };
    const fetchNodeUsage = async () => {
        setUsageLoading(true);
        const result = await tunnelMonitor({
            function: 'getTunnelData',
            getType: 'nodeUsage',
            ipsec_node_id: nodeUsage.nodeId,
        });
        if (result.success === 0) message.error(result.data.msg, 4);
        if (result.success === 1) {
            setNodeUsages(result.data);
            setUsageLoading(false);
        }
    };

    useEffect(() => {
        if (nodeUsage.isShown) fetchNodeUsage();
    }, [nodeUsage.isShown, nodeUsage.nodeId]);

    const tabActions = <Switch checkedChildren={'Hours'} unCheckedChildren={'Days'} onChange={onChangeSwitch} />;

    const onTabChange = (activeKey) => setShowSwitch(tabKeys.node === activeKey);

    const tab = (
        <>
            <span>{tabKeys.node}</span>
            {usageLoading ? <Spin className={styles.ns_margin_left_small} size={'small'} /> : ''}
        </>
    );
    return (
        <Tabs destroyInactiveTabPane={true} tabBarExtraContent={showSwitch ? tabActions : ''} onChange={onTabChange}>
            <TabPane tab={tab} key={tabKeys.node}>
                <NodeUsageGraph data={nodeUsages} isHourlyData={isHourlyData} healthType={nodeUsage.healthType} />
            </TabPane>
            <TabPane tab={tabKeys.tunnel} key={tabKeys.tunnel}>
                <Tunnel nodeId={nodeUsage.nodeId} />
            </TabPane>
            <TabPane tab={tabKeys.health} key={tabKeys.health}>
                <HealthTab nodeId={nodeUsage.nodeId} />
            </TabPane>
        </Tabs>
    );
};

const NodeUsageGraph = (nodeUsage) => {
    const [graphLabel, setGraphLabel] = useState([]);
    const [graphData, setGraphData] = useState([]);

    useEffect(() => {
        if (nodeUsage.data) {
            if (nodeUsage.isHourlyData) {
                generateHoulyData();
            } else {
                generateDaysData();
            }
        }
    }, [nodeUsage]);

    const generateHoulyData = () => {
        const label = [];
        const nodes = {
            cpu: { actual: [], worst: [] },
            memory: { actual: [], worst: [] },
            pps: { actual: [], worst: [] },
            bandwidth: { actual: [], worst: [] },
        };
        nodeUsage.data.nodeMetrics.toReversed().map((usage) => {
            let timeIndex = usage.datetime.indexOf('T');
            label.push(usage.datetime.substring(timeIndex + 1, timeIndex + 6));
            Object.keys(nodes).map((title) => {
                nodes[title].worst.push(usage['worst_case_' + title]);
                nodes[title].actual.push(usage[title]);
            });
        });
        const graphDataUsage = {};
        Object.keys(nodes).map((title) => {
            graphDataUsage[title] = [
                ndataline('Normal', nodes[title].actual, labelL, graphColors.green),
                ndataline('Failover', nodes[title].worst, labelL, graphColors.red),
            ];
        });
        setGraphLabel(label);
        setGraphData(graphDataUsage);
    };

    const generateDaysData = () => {
        const label = [];
        const nodes = {
            cpu: { min: [], avg: [], max: [] },
            memory: { min: [], avg: [], max: [] },
            pps: { min: [], avg: [], max: [] },
            bandwidth: { min: [], avg: [], max: [] },
        };
        nodeUsage.data.nodeUsage.toReversed().map((usage) => {
            if (!label.includes(usage.date)) {
                label.push(usage.date);
                Object.keys(UsageList).map((title) => {
                    Object.keys(nodes[title]).map((plot) => {
                        nodes[title][plot].push(formatValue(title, usage['daily_' + plot + '_' + title], true));
                    });
                });
            }
        });
        const graphDataUsage = {};
        Object.keys(nodes).map((title) => {
            graphDataUsage[title] = Object.keys(nodes[title]).map((plot) => {
                return ndataline(capitalizeFirstLetter(plot), nodes[title][plot], labelL, graphColors[RangeColorMapping[plot]]);
            });
        });
        setGraphLabel(label);
        setGraphData(graphDataUsage);
    };

    return (
        <>
            <Tabs className={styles.inner_tabs_box}>
                {Object.keys(graphData).map((usage) => {
                    return (
                        <TabPane tab={UsageList[usage].tabName} key={usage}>
                            <Line
                                data={{
                                    labels: graphLabel,
                                    datasets: graphData[usage],
                                }}
                                height={300}
                                options={gradientOpt(usage === 'bandwidth', usage)}
                            />
                        </TabPane>
                    );
                })}
            </Tabs>
        </>
    );
};

const Tunnel = (tunnelProps) => {
    const [loading, setLoading] = useState(true);
    const [tunnelGraphLabel, setTunnelGraphLabel] = useState([]);
    const [tunnelGraphData, setTunnelGraphData] = useState([]);

    const fetchTunnelUsage = async () => {
        const result = await tunnelMonitor({
            function: 'getTunnel',
            ipsec_node_id: tunnelProps.nodeId,
        });
        if (result.success === 0) message.error(result.data.msg, 4);
        if (result.success === 1) setTunnelData(result.data.IPSecTunnelUsage);
    };

    useEffect(() => {
        setLoading(true);
        fetchTunnelUsage();
    }, [tunnelProps]);

    const setTunnelData = (tunnel) => {
        try {
            const tunnels = {};
            const label = [];
            tunnel.map((tunnel) => {
                if (!label.includes(tunnel.date)) {
                    label.push(tunnel.date);
                }
                if (tunnels.hasOwnProperty(tunnel.tunnel_name)) {
                    tunnels[tunnel.tunnel_name].push(tunnel);
                } else {
                    tunnels[tunnel.tunnel_name] = [tunnel];
                }
            });
            let tunnelData = {};
            label.map((date) => {
                Object.entries(tunnels).map(([name, values], index) => {
                    let filterByDate = values.filter((row) => row.date === date);
                    let value = null;
                    if (filterByDate.length > 0) {
                        value = filterByDate[0];
                    }
                    if (tunnelData.hasOwnProperty(name)) {
                        tunnelData[name].daily_avg_bandwidth.push(formateNumber(value?.daily_avg_bandwidth));
                    } else {
                        tunnelData[name] = { daily_avg_bandwidth: [formateNumber(value?.daily_avg_bandwidth)], display: index % 2 === 0 };
                    }
                });
            });
            setTunnelGraphLabel(label);
            const graphColorsList = Object.keys(graphColors);
            let colorIndex = 0;
            let graphData = Object.entries(tunnelData).map(([tunnelName, plotData]) => {
                colorIndex = colorIndex === graphColorsList.length ? 0 : colorIndex;
                let graphColor = graphColorsList[colorIndex];
                colorIndex++;
                return ndataline(tunnelName, plotData.daily_avg_bandwidth, labelL, graphColors[graphColor]);
            });
            setTunnelGraphData(graphData);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    return (
        <>
            <div style={{ textAlign: 'center' }}>
                {loading ? (
                    <Spin />
                ) : (
                    <Line
                        data={{
                            labels: tunnelGraphLabel,
                            datasets: tunnelGraphData,
                        }}
                        height={300}
                        options={gradientOpt(true, 'tunnelUsage')}
                    />
                )}
            </div>
        </>
    );
};

const HealthTab = (props) => {
    const [loading, setLoading] = useState(true);
    const [tableColumn, setTableColumn] = useState([]);
    const [tableData, setTableData] = useState([]);

    const fetchHealthTrend = async () => {
        setLoading(true);
        const result = await tunnelMonitor({
            function: 'getTunnelData',
            getType: 'healthTrend',
            ipsec_node_id: props.nodeId,
        });
        if (result.success === 0) message.error(result.data.msg, 4);
        if (result.success === 1) {
            const tableColumns = [{ title: 'Date', dataIndex: 'health', key: 'health' }];
            const trendData = {
                best: { health: 'Normal' },
                worst: { health: 'Failover' },
            };
            result.data.healthTrend.toReversed().map((row) => {
                tableColumns.push({
                    title: row.date,
                    dataIndex: row.date,
                    key: row.date,
                    render: (text, record) => (
                        <>
                            {text === 1 ? (
                                <label className={styles.health_orange_text}>Orange</label>
                            ) : text === 2 ? (
                                <label className={styles.health_red_text}>Red</label>
                            ) : (
                                <label className={styles.health_green_text}>Green</label>
                            )}
                        </>
                    ),
                });
                trendData.best[row.date] = row.node_health;
                trendData.worst[row.date] = row.worst_case_node_health;
            });
            setLoading(false);
            setTableColumn(tableColumns);
            setTableData([trendData.best, trendData.worst]);
        }
    };
    useEffect(() => {
        fetchHealthTrend();
    }, [props]);
    return (
        <div style={{ textAlign: 'center' }}>
            {loading ? (
                <Spin />
            ) : (
                <Table columns={tableColumn} dataSource={tableData} size={'small'} pagination={false} scroll={{ y: 450 }}></Table>
            )}
        </div>
    );
};

const ndataline = (lineHead, lineData, align, color) => {
    return {
        label: lineHead,
        fill: false,
        pointStyle: 'circle',
        borderColor: color + ', 1)',
        borderCapStyle: 'butt',
        display: 'auto',
        borderDash: [],
        borderJoinStyle: 'miter',
        backgroundColor: color + ', 0.2)',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBorderWidth: 2,
        pointRadius: 3,
        pointHitRadius: 10,
        // borderWidth: 2,
        data: lineData,
        datalabels: align,
    };
};

const labelL = {
    anchor: 'top',
    align: 'end',
    offset: 3,
    borderRadius: 4,
    display: false,
};

const yAxes = [
    {
        ticks: {
            padding: 20,
        },
        gridLines: {
            drawBorder: false,
            display: false,
        },
    },
];

const yAxesHealth = [
    {
        min: 0,
        max: 2,
        ticks: {
            padding: 20,
            stepSize: 1,
            callback: function (value, index, ticks) {
                console.log('Ticks : ', value, index, ticks);
                switch (value) {
                    case 0:
                        return 'Green';
                    case 1:
                        return 'Orange';
                    case 2:
                        return 'Red';
                    default:
                        return value;
                }
            },
        },
        gridLines: {
            drawBorder: false,
            display: false,
        },
    },
];

const yAxesMemory = [
    {
        ticks: {
            padding: 20,
            callback: function (value, index, ticks) {
                return formatKBits(value, false);
            },
        },
        gridLines: {
            drawBorder: false,
            display: false,
        },
    },
];

const xAxes = [
    {
        barPercentage: 1.6,
        gridLines: {
            drawBorder: false,
            color: 'rgba(225,78,202,0.1)',
            zeroLineColor: 'transparent',
        },
        ticks: {
            display: true,
            padding: 20,
            fontColor: '#9a9a9a',
        },
    },
];
const toolTip = {
    backgroundColor: '#f5f5f5',
    titleFontColor: '#333',
    bodyFontColor: '#666',
    bodySpacing: 4,
    xPadding: 12,
    mode: 'index',
    intersect: 0,
    position: 'average',
    yAlign: 'center',
};

const toolTip1 = (graphName) => {
    return {
        enabled: true,
        mode: 'index',
        callbacks: {
            label: function (tooltipItems, data) {
                let label = data['datasets'][tooltipItems.datasetIndex]['label'].split('(')[0];
                // const value = data.datasets[tooltipItems.datasetIndex].data[tooltipItems.index] + ' KB';
                const value = formatKBits(data.datasets[tooltipItems.datasetIndex].data[tooltipItems.index], false);
                return label + ' : ' + value;
            },
        },
        backgroundColor: '#f5f5f5',
        titleFontColor: '#333',
        bodyFontColor: '#666',
        footerFontColor: '#566573',
        bodySpacing: 4,
        xPadding: 12,
        intersect: 0,
        position: 'average',
        yAlign: 'center',
    };
};

const gradientOpt = (formatTooltip, graphName) => {
    return {
        scales: {
            yAxes: formatTooltip ? yAxesMemory : graphName === 'healthTrend' ? yAxesHealth : yAxes,
            xAxes: xAxes,
        },
        layout: {
            padding: {
                top: 20,
            },
        },
        tooltips: formatTooltip ? toolTip1(graphName) : toolTip,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                usePointStyle: true,
                fontColor: '#333',
            },
        },
    };
};

const graphColors = {
    green: 'rgba(0, 255, 0',
    darkgreen: 'rgba(0, 153, 26',
    red: 'rgba(255, 0, 0',
    blue: 'rgba(0, 0, 255',
    skyblue: 'rgba(0, 204, 255',
    orange: 'rgba(245, 94, 0',
    grey: 'rgba(128, 128, 128',
};
export default TunnelMonitoring;
